import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { LuPlus } from 'react-icons/lu';

import { Role } from '@ndvr/auth-api';
import { BillingCycle, BillingTiming, FeeSchedule, FeeScheduleWithUsage, GUID } from '@ndvr/billing-api';

import { FilterType } from '@/cip/_openapi/portfolio-optimizer';
import { ConfirmModal } from '@/components/ConfirmModal';
import { ClientDataTable } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { Page, PageContent, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { titleCase } from 'utils';
import { useUserHasRole } from 'views/admin/dev-queries';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';

import { CreateFeeScheduleDialog } from './CreateFeeScheduleDialog';
import { useDeleteFeeSchedule, useFeeSchedules } from './fee-schedule-queries';

export const FeeSchedulesPage = () => {
    const { data: feeSchedules = [], isLoading } = useFeeSchedules();
    const deleteMutation = useDeleteFeeSchedule();
    const tenantGUIDs = useSelectedTenantGUIDs();
    const { data: canManage = false } = useUserHasRole(Role.CHIEF_COMPLIANCE_OFFICER);

    const [editingItem, setEditingItem] = useState<FeeScheduleWithUsage | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deletingGUID, setDeletingGUID] = useState<GUID<FeeSchedule> | null>(null);

    const canCreate = canManage && tenantGUIDs.length === 1;

    const openCreate = () => {
        setEditingItem(null);
        setDialogOpen(true);
    };

    const openEdit = (item: FeeScheduleWithUsage) => {
        setEditingItem(item);
        setDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (!deletingGUID) return;
        deleteMutation.mutate(deletingGUID, { onSuccess: () => setDeletingGUID(null) });
    };

    const columns = useMemo<ColumnDef<FeeScheduleWithUsage>[]>(
        () => [
            {
                accessorKey: 'feeScheduleName',
                header: 'Name',
                sortingFn: (a, b, id) =>
                    String(a.getValue(id) ?? '').localeCompare(String(b.getValue(id) ?? ''), undefined, {
                        sensitivity: 'base',
                    }),
                meta: { filterType: FilterType.STRING },
            },
            {
                accessorKey: 'feeScheduleDescription',
                header: 'Description',
                cell: ({ getValue }) => getValue<string>() ?? '—',
                meta: { filterType: FilterType.STRING },
            },
            {
                accessorKey: 'billingTiming',
                header: 'Billing Timing',
                cell: ({ getValue }) => titleCase(getValue<string>()),
                meta: {
                    filterType: FilterType.LIST,
                    filterOptionsFn: () => Object.values(BillingTiming),
                    formatOption: (v: string) => titleCase(v),
                },
            },
            {
                accessorKey: 'billingCycle',
                header: 'Billing Cycle',
                cell: ({ getValue }) => titleCase(getValue<string>()),
                meta: {
                    filterType: FilterType.LIST,
                    filterOptionsFn: () => Object.values(BillingCycle),
                    formatOption: (v: string) => titleCase(v),
                },
            },
            {
                accessorKey: 'referencingBillingGroupCount',
                header: 'Is Used',
                cell: ({ getValue }) => (getValue<number>() > 0 ? 'Yes' : 'No'),
                meta: { filterType: FilterType.BOOLEAN },
            },

            {
                id: 'actions',
                enableResizing: false,
                enableSorting: false,
                cell: ({ row }) =>
                    canManage ? (
                        <div className="flex justify-end">
                            <DropdownMenu>
                                <EllipsisDropdownMenuTrigger />
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openEdit(row.original)}>Edit</DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        disabled={row.original.referencingBillingGroupCount > 0}
                                        onClick={() => setDeletingGUID(row.original.feeScheduleGUID)}
                                    >
                                        Delete
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ) : null,
            },
        ],
        [canManage],
    );

    return (
        <Page>
            <PageTopBar />
            <PageTitleRow
                title="Fee Schedules"
                actions={
                    canManage && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span>
                                    <Button
                                        variant="outline2"
                                        size="sm"
                                        className="px-3"
                                        disabled={tenantGUIDs.length !== 1}
                                        onClick={openCreate}
                                    >
                                        <LuPlus className="h-4 w-4" /> New Fee Schedule
                                    </Button>
                                </span>
                            </TooltipTrigger>
                            {tenantGUIDs.length !== 1 && (
                                <TooltipContent>Select a single tenant to create a fee schedule</TooltipContent>
                            )}
                        </Tooltip>
                    )
                }
            />

            <PageContent>
                <ClientDataTable
                    data={feeSchedules}
                    columns={columns}
                    isLoading={isLoading}
                    emptyMessage="No fee schedules found."
                    initialState={{ sorting: [{ id: 'feeScheduleName', desc: false }] }}
                />
            </PageContent>

            {canManage && (editingItem || canCreate) && (
                <CreateFeeScheduleDialog
                    tenantGUID={editingItem ? editingItem.tenantGUID : tenantGUIDs[0]}
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    feeSchedule={editingItem ?? undefined}
                />
            )}

            <ConfirmModal
                open={!!deletingGUID}
                onOpenChange={(open) => !open && setDeletingGUID(null)}
                title="Delete Fee Schedule"
                description="Are you sure you want to delete this fee schedule? This action cannot be undone."
                confirmText="Delete"
                confirmVariant="destructive"
                onConfirm={handleDeleteConfirm}
                loading={deleteMutation.isPending}
                closeOnConfirm={false}
            />
        </Page>
    );
};
